import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import Grid from '@material-ui/core/Grid';
import InputLabel, { InputLabelProps as MuiInputLabelProps } from '@material-ui/core/InputLabel';
import { WithStyles, withStyles } from '@material-ui/core/styles';
import { OldTextField, OldTextFieldProps } from 'embeds/components/OldTextField';
import { filter, get, includes, map } from 'lodash';

import SelectField from 'embeds/components/SelectField';

import { styles } from './UserContactFieldSelect.styles';

const messages = defineMessages({
  sms: {
    id: 'components.UserContactFieldSelect.sms',
    defaultMessage: 'SMS',
  },
  voice: {
    id: 'components.UserContactFieldSelect.voice',
    defaultMessage: 'Voice',
  },
  email: {
    id: 'components.UserContactFieldSelect.email',
    defaultMessage: 'Email',
  },
  whatsapp: {
    id: 'components.UserContactFieldSelect.whatsapp',
    defaultMessage: 'WhatsApp',
  },
});

export interface UserContactFieldSelectProps extends Omit<OldTextFieldProps, 'classes'>, WithStyles<typeof styles> {
  typeOptions?: ('sms' | 'voice' | 'whatsapp' | 'email')[];
  excludedOptions?: any;
  value: any;
  label?: any;
  onChangeContactValue: Function;
  onChangeContactType: Function;
  onBlur?: any;
  error?: any;
  helperText?: any;
  InputLabelProps?: MuiInputLabelProps;
}

const UserContactFieldSelect: React.FC<UserContactFieldSelectProps> = ({
  classes,
  className,
  typeOptions,
  disabled,
  excludedOptions,
  value: inputValue,
  label,
  showRequiredMark,
  onChangeContactValue,
  onChangeContactType,
  onBlur,
  error,
  helperText,
  InputLabelProps,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const TYPE_OPTIONS = [
    { id: 'sms', label: 'SMS' },
    { id: 'voice', label: 'Voice' },
    { id: 'email', label: 'E-Mail' },
    { id: 'whatsapp', label: 'WhatsApp' },
  ];
  const contactType = get(inputValue, 'type', 'sms');
  const isTel = contactType === 'sms' || contactType === 'voice' || contactType === 'whatsapp';
  const type = isTel ? 'tel' : 'text';
  const placeholder = isTel ? '123-456-7890' : 'E-Mail';
  const mask = isTel ? 'phone' : '';
  let options = typeOptions
    ? map(typeOptions, (typeOption) => ({
        id: typeOption,
        label: formatMessage(messages[typeOption]),
      }))
    : TYPE_OPTIONS;

  if (excludedOptions && excludedOptions.length > 0) {
    options = filter(options, (option) => !includes(excludedOptions, option.id));
  }

  return (
    <Grid className={className} container spacing={8}>
      {label && (
        <InputLabel
          shrink={get(InputLabelProps, 'shrink')}
          className={get(InputLabelProps, 'className') || classes.label}
          disabled={!!disabled}
          htmlFor="patient"
        >
          {label}
          {showRequiredMark && <span className={classes.asterisk}>*</span>}
        </InputLabel>
      )}
      <Grid item xs={12}>
        <div className={classes.type}>
          <SelectField
            // @ts-ignore
            disabled={disabled}
            label=""
            name={`${inputValue}.type`}
            options={options}
            searchable={false}
            value={inputValue.type}
            onChange={(e: any) => {
              onChangeContactType(e);
            }}
          />
        </div>
        <div className={classes.value}>
          <OldTextField
            disabled={!!disabled}
            mask={mask}
            name={`${inputValue}.value`}
            onChange={(e: any) => onChangeContactValue(e)}
            placeholder={placeholder}
            type={type}
            onBlur={onBlur}
            error={error}
            helperText={helperText}
            InputLabelProps={InputLabelProps}
            value={inputValue.value}
            {...rest}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(UserContactFieldSelect);
